// 內建手繪圖示：以 24×24 網格設計，用 roughjs 繪製，與節點筆觸一致。
import { pen, fmt, type StrokeStyle } from './pen.js';
import type { Swatch } from './theme.js';

type Draw = (line: StrokeStyle, fill: StrokeStyle, seed: string) => string;

const P = (x: number, y: number) => ({ x, y });
const dot = (x: number, y: number, line: StrokeStyle, seed: string) =>
  pen.ellipse(x, y, 2.2, 2.2, { ...line, fill: line.stroke, fillStyle: 'solid' }, seed);

// 五角星頂點。
function starPoints(cx: number, cy: number, outer: number, inner: number) {
  return Array.from({ length: 10 }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    const radius = index % 2 ? inner : outer;
    return P(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
  });
}

const ICONS: Record<string, Draw> = {
  info: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.line(12, 10.5, 12, 17, l, s + 'a') + dot(12, 7, l, s + 'b'),
  warning: (l, f, s) => pen.polygon([P(12, 2.5), P(22, 20.5), P(2, 20.5)], f, s) + pen.line(12, 9, 12, 14, l, s + 'a') + dot(12, 17.2, l, s + 'b'),
  question: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.path('M9 9.5C9 6.3 15 6.3 15 9.5C15 11.6 12 11.6 12 14.2', l, s + 'a') + dot(12, 17.4, l, s + 'b'),
  idea: (l, f, s) => pen.path('M8.2 15.5C5.5 13.5 5 10.8 5.6 8.6C6.6 5 9.6 3.3 12 3.3C14.4 3.3 17.4 5 18.4 8.6C19 10.8 18.5 13.5 15.8 15.5Z', f, s)
    + pen.line(9, 18, 15, 18, l, s + 'a') + pen.line(10, 21, 14, 21, l, s + 'b'),
  star: (l, f, s) => pen.polygon(starPoints(12, 12.6, 10.5, 4.6), f, s),
  check: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.polyline([P(7, 12.5), P(10.5, 16), P(17, 8.5)], l, s + 'a'),
  cross: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.line(8, 8, 16, 16, l, s + 'a') + pen.line(16, 8, 8, 16, l, s + 'b'),
  flag: (l, f, s) => pen.polygon([P(6, 3.5), P(19.5, 7), P(6, 11.5)], f, s) + pen.line(6, 3, 6, 21.5, l, s + 'a'),
  clock: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.polyline([P(12, 6.5), P(12, 12), P(16, 14.5)], l, s + 'a'),
  calendar: (l, f, s) => pen.roundedRect(3, 5, 18, 16, 2.5, f, s) + pen.line(3, 9.8, 21, 9.8, l, s + 'a')
    + pen.line(8, 3, 8, 7, l, s + 'b') + pen.line(16, 3, 16, 7, l, s + 'c')
    + dot(8, 14, l, s + 'd') + dot(12, 14, l, s + 'e') + dot(16, 14, l, s + 'f') + dot(8, 17.5, l, s + 'g') + dot(12, 17.5, l, s + 'h'),
  pin: (l, f, s) => pen.ellipse(12, 8, 10, 10, f, s) + pen.line(12, 13, 12, 21.5, l, s + 'a'),
  target: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.ellipse(12, 12, 12, 12, l, s + 'a') + dot(12, 12, l, s + 'b'),
  heart: (l, f, s) => pen.path('M12 20C4.5 14.5 2.2 10 4.6 6.6C7 3.4 10.6 4.6 12 7.4C13.4 4.6 17 3.4 19.4 6.6C21.8 10 19.5 14.5 12 20Z', f, s),
  people: (l, f, s) => pen.ellipse(9, 8, 6.5, 6.5, f, s) + pen.path('M3 19.5C3 14 15 14 15 19.5', l, s + 'a')
    + pen.ellipse(16.5, 9, 5, 5, l, s + 'b') + pen.path('M15.5 13.6C18.5 13.4 21 15.2 21 18.5', l, s + 'c'),
  money: (l, f, s) => pen.ellipse(12, 12, 20, 20, f, s) + pen.path('M15 8.8C14 7.2 10 7 9.6 9.2C9.2 11.4 15 11 14.6 14.2C14.2 16.8 10 16.6 9 15', l, s + 'a') + pen.line(12, 5.6, 12, 18.4, l, s + 'b'),
  chart: (l, f, s) => pen.polyline([P(3, 3), P(3, 21), P(21, 21)], l, s) + pen.roundedRect(6, 13, 3.4, 8, 0.6, f, s + 'a')
    + pen.roundedRect(11, 9, 3.4, 12, 0.6, f, s + 'b') + pen.roundedRect(16, 5, 3.4, 16, 0.6, f, s + 'c'),
  book: (l, f, s) => pen.path('M3 5C7 4 10 5 12 7C14 5 17 4 21 5L21 19C17 18 14 19 12 20.5C10 19 7 18 3 19Z', f, s) + pen.line(12, 7, 12, 20.5, l, s + 'a'),
  gear: (l, f, s) => {
    const teeth = Array.from({ length: 8 }, (_, index) => {
      const angle = (index * Math.PI) / 4;
      return pen.line(12 + Math.cos(angle) * 7, 12 + Math.sin(angle) * 7, 12 + Math.cos(angle) * 10.5, 12 + Math.sin(angle) * 10.5, { ...l, strokeWidth: l.strokeWidth * 1.6 }, s + index);
    }).join('');
    return teeth + pen.ellipse(12, 12, 14, 14, f, s) + pen.ellipse(12, 12, 5, 5, l, s + 'h');
  },
  lock: (l, f, s) => pen.path('M8 11V8C8 4 16 4 16 8V11', l, s + 'a') + pen.roundedRect(4.5, 11, 15, 10, 2, f, s) + dot(12, 16, l, s + 'b'),
  mail: (l, f, s) => pen.roundedRect(3, 6, 18, 13, 2, f, s) + pen.polyline([P(3.5, 7), P(12, 13.5), P(20.5, 7)], l, s + 'a'),
};

export const ICON_NAMES = Object.keys(ICONS);

// 在 (x, y) 畫出 size×size 的圖示；線條用分支色，填色用分支淺色。
export function drawIcon(name: string, x: number, y: number, size: number, swatch: Swatch, seed: string): string {
  const draw = ICONS[name];
  if (!draw) return '';
  const line: StrokeStyle = { stroke: swatch.stroke, strokeWidth: 1.7, roughness: 0.6 };
  const fill: StrokeStyle = { ...line, fill: swatch.fill, fillStyle: 'solid' };
  return `<g transform="translate(${fmt(x)} ${fmt(y)}) scale(${fmt(size / 24)})">${draw(line, fill, seed)}</g>`;
}
